import React, { useState, useMemo, useEffect } from "react";
import { FiMapPin, FiClock, FiExternalLink } from "react-icons/fi";
import { FaTicketAlt } from "react-icons/fa";
import { motion } from "framer-motion";

import MainContainer from "../../../components/utils/MainContainer";
import Particles from "../../../components/utils/Particles";
import {
  ActionBtn,
  BandTag,
  Container,
  ControlsSection,
  FeaturedContent,
  FeaturedSection,
  FeaturedImageSection,
  FeaturedIcon,
  FeaturedTextContent,
  FeaturedBadge,
  FeaturedTitle,
  FeaturedBandName,
  FeaturedText,
  FeaturedMeta,
  FeaturedMetaItem,
  FeaturedActions,
  FeaturedActionBtn,
  FilterTab,
  FilterTabs,
  NewsActions,
  NewsCard,
  NewsContent,
  NewsDate,
  NewsExcerpt,
  NewsFooter,
  NewsGrid,
  NewsImage,
  NewsLocation,
  NewsTime,
  NewsTitle,
  NewsType,
  NoResults,
  PageHeader,
  PageSubtitle,
  PageTitle,
  SearchIcon,
  SearchInput,
  SearchWrap,
} from "./NewsPage.styles";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { selectNews } from "../../../store/slices/news/slice";
import {
  formatNewsDate,
  getNewsTypeIcon,
  getNewsTypeLabel,
  newsTypes,
} from "../../../utils/utils";
import { getNews } from "../../../store/slices/news/asyncThunks";

export default function NewsPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { data: allNews, loading } = useSelector(selectNews);
  const [search, setSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");

  useEffect(() => {
    dispatch(getNews({ page: 1, limit: 12 }));
  }, [dispatch]);

  // Ensure allNews is always an array
  const newsList = useMemo(() => {
    if (!allNews || !Array.isArray(allNews)) {
      return [];
    }
    return allNews;
  }, [allNews]);

  // Featured news
  const featuredNews = useMemo(() => {
    return newsList.find((n) => n.isFeatured) || newsList[0];
  }, [newsList]);

  // Filter and search news -  koristi useMemo umesto direktno setovanje state-a
  const filteredNews = useMemo(() => {
    let filtered = newsList;

    // Filtriranje po tipu
    if (activeFilter !== "all") {
      filtered = filtered.filter((news) => news.type === activeFilter);
    }

    // Filtriranje po pretrazi
    if (search.trim()) {
      const searchQuery = search.toLowerCase().trim();
      filtered = filtered.filter((news) => {
        const titleMatch =
          news.title && news.title.toLowerCase().includes(searchQuery);
        const bandMatch =
          news.band &&
          news.band.name &&
          news.band.name.toLowerCase().includes(searchQuery);
        const textMatch =
          news.text && news.text.toLowerCase().includes(searchQuery);
        const locationMatch =
          news.location && news.location.toLowerCase().includes(searchQuery);
        const venueMatch =
          news.venue && news.venue.toLowerCase().includes(searchQuery);

        return (
          titleMatch || bandMatch || textMatch || locationMatch || venueMatch
        );
      });
    }

    // Sortiraj po datumu (najnovije prvo)
    return filtered.sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [newsList, activeFilter, search]);

  const handleReadMore = (news) => {
    if (news.externalLink) {
      window.open(news.externalLink, "_blank");
    }
  };

  const handleTickets = (news) => {
    if (news.ticketLink) {
      window.open(news.ticketLink, "_blank");
    }
  };

  const handleFilterChange = (filter) => {
    setActiveFilter(filter);
  };

  // Loading state
  if (loading) {
    return (
      <MainContainer>
        <Particles />
        <Container>
          <PageHeader>
            <PageTitle>Muzičke novosti</PageTitle>
            <PageSubtitle>
              Pratite najnovije novosti sa muzičke scene - koncerti, albumi,
              intervjui i sve što se dešava u svetu vaših omiljenih bendova.
            </PageSubtitle>
          </PageHeader>
          <div style={{ textAlign: "center", padding: "2rem", color: "#fff" }}>
            <l-line-spinner size="20" stroke="2" speed="1" color="#fff" />

            <p>Učitavanje novosti...</p>
          </div>
        </Container>
      </MainContainer>
    );
  }

  return (
    <MainContainer>
      <Particles />
      <Container>
        <PageHeader>
          <PageTitle>Muzičke novosti</PageTitle>
          <PageSubtitle>
            Pratite najnovije novosti iz srpske muzičke scene - koncerti,
            albumi, intervjui i sve što se dešava u svetu vaših omiljenih
            bendova.
          </PageSubtitle>
        </PageHeader>

        {featuredNews && (
          <FeaturedSection
            onClick={() => {
              navigate(`/novost/${featuredNews.id}`);
              setTimeout(() => {
                window.scrollTo(0, 0);
              }, 300);
            }}
            as={motion.div}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <FeaturedContent>
              <FeaturedImageSection $backgroundImage={featuredNews.mainImage}>
                {!featuredNews.mainImage && (
                  <FeaturedIcon>
                    {React.createElement(getNewsTypeIcon(featuredNews.type), {
                      size: 80,
                    })}
                  </FeaturedIcon>
                )}
              </FeaturedImageSection>

              <FeaturedTextContent>
                <FeaturedBadge>
                  {React.createElement(getNewsTypeIcon(featuredNews.type), {
                    size: 16,
                  })}
                  Istaknuta novost
                </FeaturedBadge>

                <FeaturedBandName>{featuredNews.band.name}</FeaturedBandName>
                <FeaturedTitle>{featuredNews.title}</FeaturedTitle>

                <FeaturedMeta>
                  <FeaturedMetaItem>
                    <FiClock size={16} />
                    {formatNewsDate(featuredNews.date)} u{" "}
                    {featuredNews.time.slice(0, 5)}
                  </FeaturedMetaItem>

                  {featuredNews.location && (
                    <FeaturedMetaItem>
                      <FiMapPin size={16} />
                      {featuredNews.location}
                    </FeaturedMetaItem>
                  )}

                  {featuredNews.venue && (
                    <FeaturedMetaItem>
                      <FiMapPin size={16} />
                      {featuredNews.venue}
                    </FeaturedMetaItem>
                  )}
                </FeaturedMeta>

                <FeaturedText>
                  {featuredNews.text.slice(0, 200)}...
                </FeaturedText>

                <FeaturedActions>
                  {featuredNews.externalLink && (
                    <FeaturedActionBtn
                      $primary
                      onClick={(e) => {
                        e.stopPropagation();
                        handleReadMore(featuredNews);
                      }}
                    >
                      <FiExternalLink />
                      Pročitaj više
                    </FeaturedActionBtn>
                  )}

                  {featuredNews.ticketLink && (
                    <FeaturedActionBtn
                      onClick={(e) => {
                        e.stopPropagation();
                        handleTickets(featuredNews);
                      }}
                    >
                      <FaTicketAlt />
                      Kupi karte
                    </FeaturedActionBtn>
                  )}
                </FeaturedActions>
              </FeaturedTextContent>
            </FeaturedContent>
          </FeaturedSection>
        )}

        <ControlsSection>
          <SearchWrap>
            <SearchInput
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Pretraguj novosti, bendove, gradove..."
            />
            <SearchIcon />
          </SearchWrap>

          <FilterTabs>
            {newsTypes.map((type) => (
              <FilterTab
                key={type.id}
                $active={activeFilter === type.id}
                onClick={() => handleFilterChange(type.value)}
              >
                {type.name}
              </FilterTab>
            ))}
          </FilterTabs>
        </ControlsSection>

        {filteredNews.length === 0 ? (
          <NoResults>
            <h3>Nema rezultata</h3>
            <p>
              {newsList.length === 0
                ? "Nema dostupnih novosti."
                : "Pokušajte sa drugim pojmom pretrage ili filterom."}
            </p>
          </NoResults>
        ) : (
          <NewsGrid>
            {filteredNews.map((news) => {
              const TypeIcon = getNewsTypeIcon(news.type);
              return (
                <NewsCard
                  key={news.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  onClick={() => {
                    navigate(`/novost/${news.id}`);
                    setTimeout(() => {
                      window.scrollTo(0, 0);
                    }, 300);
                  }}
                >
                  <NewsImage $backgroundImage={news.mainImage}>
                    <NewsDate>{formatNewsDate(news.date)}</NewsDate>
                    <NewsType type={news.type}>
                      <TypeIcon size={14} />
                      {getNewsTypeLabel(news.type)}
                    </NewsType>
                  </NewsImage>

                  <NewsContent>
                    <BandTag>{news.band.name}</BandTag>
                    <NewsTitle>{news.title}</NewsTitle>

                    {news.location && news.venue && (
                      <NewsLocation>
                        <FiMapPin />
                        <span>
                          {news.venue}, {news.location}
                        </span>
                      </NewsLocation>
                    )}

                    {news.time && (
                      <NewsTime>
                        <FiClock />
                        <span>
                          {formatNewsDate(news.date)} u {news.time.slice(0, 5)}
                        </span>
                      </NewsTime>
                    )}

                    <NewsExcerpt>{news.text}</NewsExcerpt>

                    {(news.ticketLink || news.externalLink) && (
                      <NewsFooter>
                        <NewsActions>
                          {news.externalLink && (
                            <ActionBtn
                              $primary
                              onClick={(e) => {
                                e.stopPropagation();
                                handleReadMore(news);
                              }}
                            >
                              <FiExternalLink />
                              Opširnije
                            </ActionBtn>
                          )}

                          {news.ticketLink && (
                            <ActionBtn
                              onClick={(e) => {
                                e.stopPropagation();
                                handleTickets(news);
                              }}
                            >
                              <FaTicketAlt />
                              Karte
                            </ActionBtn>
                          )}
                        </NewsActions>
                      </NewsFooter>
                    )}
                  </NewsContent>
                </NewsCard>
              );
            })}
          </NewsGrid>
        )}
      </Container>
    </MainContainer>
  );
}
